import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Activity, 
  Clock, 
  Zap,
  CheckCircle,
  AlertCircle,
  Download,
  RefreshCw,
  MessageSquare
} from 'lucide-react'
import { useSwarm } from '../hooks/useSwarms'
import { useSwarmStore, useAuthStore } from '../stores'
import { artifactApi } from '../utils/api'
import { formatDistanceToNow } from 'date-fns'
import type { WebSocketMessage } from '../types'

export default function SwarmDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: swarm, isLoading, refetch } = useSwarm(id!)
  const { token } = useAuthStore()
  const { liveUpdates, handleWebSocketMessage } = useSwarmStore()
  const [activeTab, setActiveTab] = useState<'overview' | 'agents' | 'results' | 'logs'>('overview')

  // Handle export results
  const handleExport = () => {
    if (!swarm?.results) return
    
    const exportData = {
      swarm: {
        id: swarm.id,
        name: swarm.name,
        task: swarm.task,
        status: swarm.status,
        createdAt: swarm.createdAt,
        completedAt: swarm.completedAt,
      },
      results: swarm.results,
    }
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `swarm-${swarm.id}-results.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Connect WebSocket when swarm is active
  useEffect(() => {
    if (id && token && swarm?.status && ['planning', 'executing', 'synthesizing'].includes(swarm.status)) {
      // Use Sec-WebSocket-Protocol header for auth instead of URL
      const ws = new WebSocket(
        `${import.meta.env.VITE_WS_URL || 'wss://mindcore.mindaeon.com'}/ws/swarms/${id}`,
        ['auth', token] // Send token via subprotocol
      )
      
      ws.onopen = () => {
        console.log('WebSocket connected')
      }
      
      ws.onmessage = (event) => {
        const message: WebSocketMessage = JSON.parse(event.data)
        handleWebSocketMessage(message)
        
        // Refetch swarm data on completion
        if (message.type === 'swarm.complete' || message.type === 'swarm.error') {
          refetch()
        }
      }
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }
      
      ws.onclose = () => {
        console.log('WebSocket disconnected')
      }

      return () => {
        ws.close()
      }
    }
  }, [id, token, swarm?.status])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    )
  }

  if (!swarm) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900">Swarm not found</h2>
        <Link to="/swarms" className="mt-4 text-primary-600 hover:text-primary-700">
          Back to swarms
        </Link>
      </div>
    )
  }

  const isActive = ['planning', 'executing', 'synthesizing'].includes(swarm.status)
  const progress = swarm.progress.totalAgents > 0
    ? Math.round((swarm.progress.completedAgents / swarm.progress.totalAgents) * 100)
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center">
          <Link
            to="/swarms"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{swarm.name}</h1>
            <div className="flex items-center mt-1 text-sm text-gray-500">
              <StatusBadge status={swarm.status} />
              <span className="mx-2">•</span>
              <Clock className="h-4 w-4 mr-1" />
              Created {formatDistanceToNow(new Date(swarm.createdAt))} ago
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          {swarm.results && (
            <button 
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar (for active swarms) */}
      {isActive && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-amber-500" />
              {swarm.progress.currentPhase === 'decomposing' && 'Analyzing task...'}
              {swarm.progress.currentPhase === 'deploying' && 'Deploying agents...'}
              {swarm.progress.currentPhase === 'executing' && 'Agents working...'}
              {swarm.progress.currentPhase === 'synthesizing' && 'Synthesizing results...'}
            </h2>
            <span className="text-sm font-medium text-gray-600">{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-primary-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
            <span>{swarm.progress.completedAgents} / {swarm.progress.totalAgents} agents complete</span>
            <span>{swarm.progress.activeAgents} active</span>
          </div>
        </div>
      )}

      {/* Live Updates */}
      {liveUpdates.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Live Updates</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {Array.from(liveUpdates.entries()).reverse().map(([key, update]) => (
              <div key={key} className="text-sm text-blue-700">
                {update.type === 'phase_change' && `Phase: ${update.phase}`}
                {update.type === 'decomposition_complete' && `Created ${update.subTasks?.length || 0} sub-tasks`}
                {update.type === 'agent_deployed' && `Deployed ${update.role} agent`}
                {update.type === 'agent_complete' && `Agent complete (${update.progress?.completed}/${update.progress?.total})`}
                {update.type === 'quality_result' && `Quality score: ${Math.round(update.score * 100)}%`}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'agents', 'results', 'logs'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === 'overview' && (
          <OverviewTab swarm={swarm} />
        )}
        {activeTab === 'agents' && (
          <AgentsTab swarm={swarm} />
        )}
        {activeTab === 'results' && swarm.results && (
          <ResultsTab swarm={swarm} />
        )}
        {activeTab === 'results' && !swarm.results && (
          <div className="text-center py-12 text-gray-500">
            <p>Results will be available once the swarm completes.</p>
          </div>
        )}
        {activeTab === 'logs' && (
          <LogsTab swarm={swarm} />
        )}
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const statusClasses: Record<string, string> = {
    idle: 'bg-gray-100 text-gray-700',
    planning: 'bg-blue-100 text-blue-700',
    executing: 'bg-amber-100 text-amber-700',
    synthesizing: 'bg-purple-100 text-purple-700',
    complete: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusClasses[status] || statusClasses.idle}`}>
      {status}
    </span>
  )
}

function OverviewTab({ swarm }: { swarm: any }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="font-medium text-gray-900 mb-4">Task Description</h3>
        <p className="text-gray-600 whitespace-pre-wrap">{swarm.task}</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="font-medium text-gray-900 mb-4">Configuration</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-500">Max Agents</span>
            <span className="font-medium">{swarm.config.maxAgents}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Model</span>
            <span className="font-medium">{swarm.config.model}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Temperature</span>
            <span className="font-medium">{swarm.config.temperature}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Tools</span>
            <span className="font-medium">{swarm.config.tools.join(', ')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Fact Checking</span>
            <span className="font-medium">{swarm.config.enableFactChecking ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Self-Improvement</span>
            <span className="font-medium">{swarm.config.enableSelfImprovement ? 'Enabled' : 'Disabled'}</span>
          </div>
        </div>
      </div>

      {swarm.results?.metrics && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
          <h3 className="font-medium text-gray-900 mb-4">Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Total Tokens"
              value={swarm.results.metrics.totalTokens.toLocaleString()}
              icon={MessageSquare}
            />
            <MetricCard
              label="Tool Calls"
              value={swarm.results.metrics.totalToolCalls}
              icon={Zap}
            />
            <MetricCard
              label="Duration"
              value={`${Math.round(swarm.results.metrics.totalDuration / 1000)}s`}
              icon={Clock}
            />
            <MetricCard
              label="Parallel Efficiency"
              value={`${swarm.results.metrics.parallelEfficiency}%`}
              icon={Activity}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function AgentsTab({ swarm }: { swarm: any }) {
  // This would fetch agents from API
  const agents = swarm.results?.agentResults || []

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tokens</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tools</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {agents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                  No agent data available
                </td>
              </tr>
            ) : (
              agents.map((agent: any, index: number) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                      {agent.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                    {agent.task || 'Sub-task execution'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={agent.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {agent.tokensUsed?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {agent.toolCalls}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ResultsTab({ swarm }: { swarm: any }) {
  // Handle artifact download
  const handleDownload = async (artifact: any) => {
    try {
      await artifactApi.download(artifact.storageKey, artifact.name)
    } catch (error) {
      console.error('Download failed:', error)
      alert(`Download failed: ${error instanceof Error ? error.message : String(error)}`)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="font-medium text-gray-900 mb-4">Summary</h3>
        <div className="prose max-w-none">
          <div className="whitespace-pre-wrap text-gray-600">
            {swarm.results.summary}
          </div>
        </div>
      </div>

      {swarm.results.artifacts?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="font-medium text-gray-900 mb-4">Artifacts</h3>
          <div className="space-y-2">
            {swarm.results.artifacts.map((artifact: any) => (
              <div 
                key={artifact.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="font-medium text-gray-900">{artifact.name}</span>
                </div>
                <button 
                  onClick={() => handleDownload(artifact)}
                  className="text-primary-600 hover:text-primary-700 text-sm"
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function LogsTab({ swarm }: { swarm: any }) {
  return (
    <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-gray-300 max-h-96 overflow-y-auto">
      <div className="space-y-1">
        <div className="text-green-400">[{new Date(swarm.createdAt).toISOString()}] Swarm initialized</div>
        <div className="text-blue-400">[{new Date(swarm.createdAt).toISOString()}] Starting orchestration...</div>
        {swarm.status === 'complete' && (
          <div className="text-green-400">[{new Date(swarm.completedAt).toISOString()}] Swarm completed successfully</div>
        )}
        {swarm.status === 'error' && (
          <div className="text-red-400">[{new Date().toISOString()}] Swarm failed with error</div>
        )}
      </div>
    </div>
  )
}

function MetricCard({ label, value, icon: Icon }: { label: string; value: string; icon: any }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center">
        <Icon className="h-5 w-5 text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
    </div>
  )
}
